

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaShieldAlt,
  FaPhoneAlt,
  FaBook,
  FaClipboardCheck,
  FaCog,
  FaServer,
  FaCalendarAlt,
  FaList,
  FaBullhorn,
  FaArrowRight,
} from "react-icons/fa";

import { useApiRequestMutation } from "../../services/apiService";
import { useUserAccess } from "../Context/UserAccessContext";
const Dashboard = () => {
  const navigate = useNavigate();
  const [apiRequest] = useApiRequestMutation();

  // Dashboard counts from API
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalRoles: 0,
    totalCallLogs: 0,
    totalKnowledgeBase: 0,
    totalQASections: 0,
    totalCategories: 0,
    totalCallStatuses: 0,
    totalShifts: 0,
    totalQueues: 0,
    totalCampaigns: 0,
  });
  const { userAccess } = useUserAccess();
  // Fetch dashboard metrics
  const fetchDashboardMetrics = async () => {
    try {
      const res = await apiRequest({
        url: "/admin/getDashboardMetrics",
        method: "GET",
      }).unwrap();

      const data = res?.data || {};

      setDashboardData({
        totalUsers: Number(
          data?.user_management?.total_users
        ) || 0,

        totalRoles: Number(
          data?.role_management?.total_roles
        ) || 0,

        totalCallLogs: Number(
          data?.call_logs?.total_call_logs
        ) || 0,

        totalKnowledgeBase: Number(
          data?.knowledge_base?.total_knowledgebase
        ) || 0,

        totalQASections: Number(
          data?.qa_section?.total_qa_sections
        ) || 0,

        totalCategories: Number(
          data?.category?.total_categories
        ) || 0,

        totalCallStatuses: Number(
          data?.call_status?.total_call_statuses
        ) || 0,

        totalShifts: Number(
          data?.shift?.total_shifts
        ) || 0,

        totalQueues: Number(
          data?.queue?.total_queues
        ) || 0,

        totalCampaigns: Number(
          data?.campaign?.total_campaigns
        ) || 0,
      });
    } catch (error) {
      console.error("Dashboard API Error:", error);
    }
  };

  useEffect(() => {
    fetchDashboardMetrics();
  }, []);

  // Dashboard modules and their respective API counts
  const accessibleModules = [
    {
      id: "user-management",
      title: "User Management",
      description: "Manage users and reporting hierarchy.",
      icon: FaUsers,
      path: "/user/list",
      count: dashboardData.totalUsers,
      menuId: 2,
    },
    {
      id: "roles-permissions",
      title: "Roles & Permissions",
      description: "Manage roles and access permissions.",
      icon: FaShieldAlt,
      path: "/roles",
      count: dashboardData.totalRoles,
      menuId: 3,
    },
    {
      id: "call-logs",
      title: "Call Logs",
      description: "View call history and recordings.",
      icon: FaPhoneAlt,
      path: "/callLogs",
      count: dashboardData.totalCallLogs,
      menuId: 4,
    },
    {
      id: "knowledge-base",
      title: "Knowledge Base",
      description: "Manage knowledge articles and answers.",
      icon: FaBook,
      path: "/knowledgeBase",
      count: dashboardData.totalKnowledgeBase,
      menuId: 5,
    },
    {
      id: "qa-management",
      title: "QA Management",
      description: "Manage quality assurance activities.",
      icon: FaClipboardCheck,
      path: "/qaSection",
      count: dashboardData.totalQASections,
      menuId: 6,
    },
    {
      id: "configuration",
      title: "Configuration",
      description: "Manage application configuration.",
      icon: FaCog,
      path: "/category",
      count: dashboardData.totalCategories,
      menuId: 7,
    },
    {
      id: "system-management",
      title: "System Management",
      description: "Manage system operations.",
      icon: FaServer,
      path: "/callStatus",
      count: dashboardData.totalCallStatuses,
      menuId: 8,
    },
    {
      id: "shift-management",
      title: "Shift Management",
      description: "Manage shifts and schedules.",
      icon: FaCalendarAlt,
      path: "/shiftManagement",
      count: dashboardData.totalShifts,
      menuId: 9,
    },
    {
      id: "queue-management",
      title: "Queue Management",
      description: "Manage queues and assignments.",
      icon: FaList,
      path: "/queue",
      count: dashboardData.totalQueues,
      menuId: 10,
    },
    {
      id: "campaigns",
      title: "Campaigns",
      description: "Manage campaigns and queues.",
      icon: FaBullhorn,
      path: "/campaign/campaign-list",
      count: dashboardData.totalCampaigns,
      menuId: 11,
    },
  ];
  const dashboardModules = accessibleModules.filter((module) =>
    userAccess?.includes(Number(module.menuId))
  );
  return (
    <div>
      {/* Breadcrumb */}
      <div className="page-header">
        <div className="page-header-left d-flex align-items-center">
          <ul className="breadcrumb mb-0">
            <li className="breadcrumb-item">Dashboard</li>
            <li className="breadcrumb-item active">Home</li>
          </ul>
        </div>
      </div>

      <div className="main-content">
        {/* Page heading */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-bold text-dark mb-1">
              Admin Dashboard
            </h4>

            <p className="text-muted mb-0">
              Access your admin modules from one place.
            </p>
          </div>
        </div>

        {/* Dashboard summary cards */}
        <div className="row g-4">
          {dashboardModules.map((module) => {
            const Icon = module.icon;

            return (
              <div
                className="col-12 col-sm-6 col-lg-4 col-xxl-3"
                key={module.id}
              >
                <button
                  type="button"
                  className="card dashboard-navigation-card h-100 w-100 text-start border-0 text-muted mb-0"
                  onClick={() => navigate(module.path)}
                >
                  <div className="card-body p-4 mb-0">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="dashboard-navigation-icon">
                        <Icon size={23} />
                      </div>

                      <FaArrowRight className="dashboard-navigation-arrow" />
                    </div>

                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div className="flex-grow-1">
                        <h6 className="fw-semibold mb-2">
                          {module.title}
                        </h6>

                        <p className="text-muted fs-13 mb-0">
                          {module.description}
                        </p>
                      </div>

                      <span className="badge rounded-pill bg-primary-subtle text-primary px-3 py-2 fs-5 flex-shrink-0">
                        {module.count}
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

